import React from 'react';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip, Typography } from '@mui/material';
import { MdDeleteForever, MdEdit, MdVisibility } from 'react-icons/md';

export const FONT = 'Mulish, sans-serif';

const STATUS_STYLES = {
  active: { bg: '#dcfce7', fg: '#166534', label: 'Active' },
  inactive: { bg: '#fef3c7', fg: '#92400e', label: 'Inactive' },
  archived: { bg: '#f1f5f9', fg: '#475569', label: 'Archived' },
};

export const StatusPill = ({ status }) => {
  const key = String(status || 'inactive').toLowerCase();
  const s = STATUS_STYLES[key] || { bg: '#f1f5f9', fg: '#475569', label: status || '-' };
  return <Chip size="small" label={s.label} sx={{ fontFamily: FONT, fontWeight: 700, height: 24, background: s.bg, color: s.fg }} />;
};

export const AudiencePill = ({ value }) => {
  const key = String(value || 'all').toLowerCase();
  const label = key === 'b2b' ? 'B2B' : key === 'b2c' ? 'B2C' : 'Everyone';
  return <Chip size="small" label={label} sx={{ fontFamily: FONT, fontWeight: 700, height: 22, fontSize: 11, background: '#e0e7ff', color: '#3730a3' }} />;
};

export const PositionBadge = ({ position }) => (
  <Box sx={{ minWidth: 28, height: 28, px: 0.75, borderRadius: '8px', background: '#0f172a', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT, fontWeight: 800, fontSize: 11.5 }}>
    #{position ?? '-'}
  </Box>
);

const actionSx = (color, bg) => ({ color, background: bg, borderRadius: '8px', '&:hover': { background: bg, filter: 'brightness(0.95)' } });

// View / Edit / Delete icon buttons used by every card.
export const CardActions = ({ onView, onEdit, onDelete }) => (
  <Box sx={{ display: 'flex', gap: 0.75 }} onClick={(e) => e.stopPropagation()}>
    {onView && (
      <Tooltip title="View" arrow>
        <IconButton size="small" onClick={onView} sx={actionSx('#4f46e5', 'rgba(79,70,229,.1)')}><MdVisibility size={16} /></IconButton>
      </Tooltip>
    )}
    {onEdit && (
      <Tooltip title="Edit" arrow>
        <IconButton size="small" onClick={onEdit} sx={actionSx('#ca8a04', '#fefce8')}><MdEdit size={16} /></IconButton>
      </Tooltip>
    )}
    {onDelete && (
      <Tooltip title="Delete" arrow>
        <IconButton size="small" onClick={onDelete} sx={actionSx('#dc2626', 'rgba(220,38,38,.1)')}><MdDeleteForever size={18} /></IconButton>
      </Tooltip>
    )}
  </Box>
);

export const cardSx = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '16px',
  overflow: 'hidden',
  border: '1px solid #e2e8f0',
  background: '#fff',
  boxShadow: '0 4px 16px rgba(15,23,42,.05)',
  transition: 'transform .15s, box-shadow .15s',
  '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 24px rgba(15,23,42,.12)' },
};

export const ConfirmDelete = ({ target, noun, label, busy, onCancel, onConfirm }) => (
  <Dialog open={!!target} onClose={() => !busy && onCancel()} PaperProps={{ sx: { borderRadius: '14px', p: 1, minWidth: 380 } }}>
    <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800 }}>Delete {noun}?</DialogTitle>
    <DialogContent>
      <Typography sx={{ fontFamily: FONT }}>
        {label ? <><b>{label}</b> will be</> : 'This will be'} removed from the mobile app. This can't be undone from the panel.
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
      <Button onClick={onCancel} disabled={busy} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
      <Button onClick={onConfirm} disabled={busy} variant="contained" sx={{ textTransform: 'none', fontWeight: 700, background: '#dc2626', '&:hover': { background: '#b91c1c' } }}>
        {busy ? 'Deleting…' : 'Yes, delete'}
      </Button>
    </DialogActions>
  </Dialog>
);

// Product thumbnails shown when you open a layout or a tab.
export const ProductGrid = ({ items, emptyText }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 2 }}>
    {items.length > 0 ? (
      items.map((item) => (
        <Box key={item.key} sx={{ borderRadius: '14px', overflow: 'hidden', border: '1px solid #e2e8f0', background: '#fff', boxShadow: '0 2px 8px rgba(15,23,42,.04)' }}>
          <Box sx={{ position: 'relative', width: '100%', paddingBottom: '100%', background: '#f1f5f9' }}>
            <img src={item.image || '/img/placeholder.png'} alt={item.name || `Product ${item.key + 1}`} style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover' }} />
          </Box>
          {item.name && (
            <Typography sx={{ p: 1.25, fontFamily: FONT, fontSize: 12, fontWeight: 700, color: '#0f172a', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {item.name}
            </Typography>
          )}
        </Box>
      ))
    ) : (
      <Typography sx={{ fontFamily: FONT, color: '#64748b' }}>{emptyText}</Typography>
    )}
  </Box>
);
